import React, { useMemo, useCallback, useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import useQueryParams from 'hooks/useQueryParams'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image/Image'
import useOfferList from 'hooks/Offers/useOfferList'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AssistChip from 'LuxKit/Chips/AssistChip'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { replace } from 'connected-react-router'
import HTMLRenderer from 'react-html-renderer'
import { useLocation } from 'react-router'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'
import useToggle from 'hooks/useToggle'
import CruiseVerticalUSPs from 'components/Cruises/CruiseOfferPage/content/BookWithConfidence/CruiseVerticalUSPs'
import { useCruiseLineByPageSlug } from 'hooks/Cruise/useCruiseLine'
import CruiseOfferListNoResults from 'components/Cruises/Common/CruiseOfferListNoResults'
import { useIntl } from 'react-intl'

import CruiseFiltersDropdown from './CruiseFiltersDropdown'
import CruiseSortDropdown from '../SearchPage/Filters/Inputs/CruiseSortDropdown'
import CruiseSearchFiltersDrawer from '../SearchPage/Filters/CruiseSearchFiltersDrawer'
import CruiseLineHero from './CruiseLineHero'
import CruiseLineFlashOffersCarousel from './CruiseLineFlashOffersCarousel'
import CruiseLineHeadData from './CruiseLineHeadData'
import { replaceWithRegion } from 'actions/NavigationActions'

const PRINCESS_CRUISES_SLUG = 'p&o-cruises-australia'

const HeaderGrid = styled.div`
  display: grid;
  grid-gap: ${rem(12)};
  grid-template-areas:
    "header-title header-title header-logo"
    "header-description header-description header-description"
  ;

  ${mediaQueryUp.tablet} {
    column-gap: ${rem(60)};
    grid-template-areas:
      "header-title header-title header-logo"
      "header-description header-description header-logo"
    ;
  }
`

const HeaderTitleArea = styled.div`
  grid-area: header-title;
`

const HeaderDescriptionArea = styled.div`
  grid-area: header-description;
`

const HeaderLogoArea = styled.div`
  grid-area: header-logo;
  display: flex;
  justify-content: flex-end;
`

const HeaderLogoImage = styled(Image)`
  width: ${rem(100)};

  ${mediaQueryUp.tablet} {
    width: ${rem(150)};
  }
`

const PageContent = styled.div`
  padding: ${rem(40)} 0;
`

interface Props extends RouteComponentProps<{ cruiseLineSlug: string }> {}

function CruiseLineLandingPage({ match }: Props) {
  const { cruiseLineSlug } = match.params
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const [showFilters, toggleFilters] = useToggle(false)
  const queryParams = useQueryParams()
  const sortValue = queryParams.get('sortBy') ?? ''

  const initialState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [queryParams])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } =
    useGlobalCruisesSearchContext({
      ...GLOBAL_SEARCH_INITIAL_STATE,
      ...initialState,
    })

  const onSortChange = useCallback((nextSort: App.OfferListSortByOptions) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const [cruiseLine, fetchingCruiseLines] = useCruiseLineByPageSlug(cruiseLineSlug)

  const filters = useMemo((): App.OfferListFilters => {
    const durationMin = queryParams.get('durationMin')
    const durationMax = queryParams.get('durationMax')

    return {
      offerTypes: ['cruise'],
      destinationId: queryParams.get('destinationId') ?? undefined,
      destinationIds: queryParams.getAll('destinationIds'),
      destinationNames: queryParams.getAll('destinationNames'),
      departurePlaceId: queryParams.get('portId') ?? undefined,
      departureNames: queryParams.getAll('departureNames') ?? undefined,
      departureIds: queryParams.getAll('departureIds') ?? undefined,
      durationMin: durationMin ? parseInt(durationMin) : undefined,
      durationMax: durationMax ? parseInt(durationMax) : undefined,
      cruiseLines: cruiseLine?.code ? [cruiseLine.code] : undefined,
      departureMonths: queryParams.get('departureMonths')?.split(','),
      rateCodes: queryParams.getAll('rateCodes'),
      cruiseShips: queryParams.getAll('cruiseShips'),
      cabinTypes: queryParams.getAll('cabinTypes'),
      isSpecialOffers: Boolean(queryParams.get('isSpecialOffers')),
      isLuxExclusivePromotion: Boolean(queryParams.get('isLuxExclusivePromotion')),
      isLuxPlus: Boolean(queryParams.get('isLuxPlus')),
      sortBy: queryParams.get('sortBy') as App.OfferListSortByOptions ?? undefined,
    }
  }, [queryParams, cruiseLine])

  const list = useOfferList(filters)

  const interstitials = useMemo((): Array<OfferListV2Item> => ([{
    id: 'heading',
    position: 'start',
    show: 'hasOffers',
  }, {
    id: 'empty',
    position: 'start',
    show: 'hasNoOffers',
    element: <CruiseOfferListNoResults />,
  }]), [])

  const getRedirectUrl = useCallback((cruiseLine: App.CruiseLine | null) => {
    const params = new URLSearchParams()
    if (cruiseLineSlug === PRINCESS_CRUISES_SLUG) {
      params.set('cruiseLines', 'CCL')
    } else if (cruiseLine?.code) {
      params.set('cruiseLines', cruiseLine.code)
    }

    return `/search/cruises?${params.toString()}`
  }, [cruiseLineSlug])

  // p&o-cruises-australia is the slug for P&O Cruises,
  // but we want to redirect to CCL (Carnival Cruise Line)
  // because P&O Cruises is now part of Carnival Cruise Line
  // Also, we want to redirect to the search page when the cruise line have landing page disabled
  useEffect(() => {
    if (
      (cruiseLine && !cruiseLine.lpEnabled) ||
      cruiseLineSlug === PRINCESS_CRUISES_SLUG
    ) {
      const redirectUrl = getRedirectUrl(cruiseLine)
      dispatch(replaceWithRegion(redirectUrl))
    }
  }, [fetchingCruiseLines, cruiseLine, cruiseLineSlug, getRedirectUrl, dispatch])

  return <div>
    {cruiseLine && <GlobalSearchContextProvider
      state={{
        ...GLOBAL_SEARCH_INITIAL_STATE,
        ...globalCruisesSearchState,
      }}
      dispatch={globalCruisesSearchDispatch}
    >
      <CruiseLineHeadData cruiseLine={cruiseLine} />
      <CruiseLineHero cruiseLine={cruiseLine} />

      <Pane>
        <PageContent>
          <LayoutContainer>
            <HeaderGrid>
              <HeaderTitleArea>
                <Heading variant="heading3">
                  {intl.formatMessage(
                    {
                      id: 'OLz6b1aO7C',
                      defaultMessage: '<italic>Why</italic> {cruiseLineName}?',
                      description: 'Heading asking why choose this cruise line',
                    },
                    {
                      cruiseLineName: cruiseLine.name,
                      italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    },
                  )}
                </Heading>
              </HeaderTitleArea>

              <HeaderDescriptionArea>
                <Group direction="vertical" gap={20} tabletGap={32}>
                  <BodyText variant="medium">
                    <LargeContentToggle
                      maxHeight={208}
                      showMoreText={intl.formatMessage({
                        id: 'en8nXOAAiI',
                        defaultMessage: 'Read more',
                        description: 'Text for expanding content toggle',
                      })}
                      showLessText={intl.formatMessage({
                        id: 'hmdOPvj5Hh',
                        defaultMessage: 'Read less',
                        description: 'Text for collapsing content toggle',
                      })}
                    >
                      <HTMLRenderer html={cruiseLine.description} />
                    </LargeContentToggle>
                  </BodyText>
                </Group>
              </HeaderDescriptionArea>

              <HeaderLogoArea>
                <HeaderLogoImage id={cruiseLine.imageId} />
              </HeaderLogoArea>
            </HeaderGrid>
          </LayoutContainer>
        </PageContent>
      </Pane>

      <CruiseLineFlashOffersCarousel cruiseLine={cruiseLine} />

      <LayoutContainer>
        <PageContent>
          <VerticalSpacer gap={16}>
            <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" gap={16}>
              <Heading variant="heading2">
                {intl.formatMessage(
                  {
                    id: 'h0qBDSnAp3',
                    defaultMessage: 'Explore our incredible {cruiseLineName} offers',
                    description: 'Heading for cruise line offers section',
                  },
                  { cruiseLineName: cruiseLine.name },
                )}
              </Heading>

              <BodyText variant="large">
                {list.fetching && intl.formatMessage({
                  id: 'gnMH+Pw3gd',
                  defaultMessage: 'Searching for cruises',
                  description: 'Loading state text while searching for cruises',
                })}
                {!list.fetching && intl.formatMessage(
                  {
                    id: 'dMIT6bbGXw',
                    defaultMessage: '{count, plural, =0 {No cruises available} one {# cruise available} other {# cruises available}}',
                    description: 'Number of cruises available',
                  },
                  { count: list.offerIds.length },
                )}
                {list.fetching && <LoadingAnimation size={8} />}
              </BodyText>
            </Group>

            <Group direction="horizontal" horizontalAlign="start" tabletHorizontalAlign="space-between" gap={8}>
              <Group direction="horizontal" gap={8}>
                <AssistChip
                  variant="filled"
                  startIcon={<LineFilterAltIcon />}
                  size="medium"
                  onClick={toggleFilters}
                >
                  <CSSBreakpoint min="tablet">
                    {intl.formatMessage({
                      id: 'NjpfjaUSAP',
                      defaultMessage: 'Filter offers',
                      description: 'Text for filter button on tablet and larger screens',
                    })}
                  </CSSBreakpoint>
                  <CSSBreakpoint max="mobile">
                    {intl.formatMessage({
                      id: 'GQAkJ6IG8i',
                      defaultMessage: 'Filter',
                      description: 'Text for filter button on mobile screens',
                    })}
                  </CSSBreakpoint>
                </AssistChip>

                <CruiseFiltersDropdown filters={filters} />
              </Group>

              <CSSBreakpoint min="desktop">
                <CruiseSortDropdown sort={sortValue} onChange={onSortChange} />
              </CSSBreakpoint>
            </Group>

            <OfferList
              filters={filters}
              interstitials={interstitials}
              tileStyle="search"
            />
          </VerticalSpacer>
        </PageContent>
      </LayoutContainer>

      <Pane paletteType="inverse">
        <PageContent>
          <LayoutContainer>
            <Heading colour="neutral-one" variant="heading3" align="start">
              {intl.formatMessage({
                id: '8oyIPo1bCl',
                defaultMessage: 'Why you should book with us',
                description: 'Heading for why book with us section',
              })}
            </Heading>
            <CruiseVerticalUSPs />
          </LayoutContainer>
        </PageContent>
      </Pane>

      <CruiseSearchFiltersDrawer
        open={showFilters}
        onClose={toggleFilters}
        searchPage={`/cruise-line/${cruiseLineSlug}`}
        isCruiselineLandingPage
      />
    </GlobalSearchContextProvider>}

    {fetchingCruiseLines && <LayoutContainer>
      <PageContent>
        <Group direction="vertical" horizontalAlign="center">
          <LoadingAnimation />
        </Group>
      </PageContent>
    </LayoutContainer>}
  </div>
}

export default CruiseLineLandingPage
